import { Card, Tag, Title, Text } from '@nimbus-ds/components'

interface FunnelStep {
  step_id: string
  step_name: string
  overall: {
    count: number
    percentage: number
  }
  control: {
    count: number
    percentage: number
    conversion_rate_reached?: number
  }
  treatment: {
    count: number
    percentage: number
    conversion_rate_reached?: number
  }
  lift_percentage?: number
  p_value?: number
  is_significant?: boolean
  confidence_level?: number
  bb_rope?: { p_better: number | null; p_worse: number | null; verdict: 'treatment_better' | 'control_better' | 'inconclusive'; rope: number }
}

interface FunnelMetricsCardProps {
  funnelData: FunnelStep[]
  /** Negative experiment: the change-under-test lives in the control variant, so the
   *  control column is the Hypothesis and the treatment column is the Baseline. */
  isNegative?: boolean
}

/**
 * Per-step reach, one compact table.
 *
 * What it answers: "does the hypothesis move people further down the funnel than the baseline?" —
 * per step, with the SAME BB+ROPE engine as the header so a step can never claim a confident win the
 * header denies. Only confident verdicts get a chip; an inconclusive step stays quiet (a chip per step
 * saying "sin diferencia concluyente" seven times was noise, not information).
 *
 * Deliberately NOT here (they were on the previous card and none of them was actionable on an
 * experiment): store-style "Alto drop-off" badges, per-step "Conversión a meta" (it was the PURCHASE
 * rate among reachers, mislabelled as the goal), "Lift en retención", and a "Resumen del Funnel" whose
 * end-to-end rate used a fourth denominator (purchases ÷ sessions that hit step 1) that no other
 * section shares.
 */
export function FunnelMetricsCard({ funnelData, isNegative = false }: FunnelMetricsCardProps) {
  if (!funnelData || funnelData.length === 0) return null

  const hypKey: 'control' | 'treatment' = isNegative ? 'control' : 'treatment'
  const baseKey: 'control' | 'treatment' = isNegative ? 'treatment' : 'control'
  const baseHeader = isNegative ? 'Tratamiento (línea base)' : 'Control (línea base)'
  const hypHeader = isNegative ? 'Control (hipótesis)' : 'Tratamiento (hipótesis)'

  const verdictChip = (bb?: FunnelStep['bb_rope']) => {
    if (!bb || bb.p_better == null) return null
    if (bb.verdict !== 'treatment_better' && bb.verdict !== 'control_better') return null
    const treatmentWon = bb.verdict === 'treatment_better'
    const hypothesisWon = isNegative ? !treatmentWon : treatmentWon
    const p = Math.max(bb.p_better ?? 0, bb.p_worse ?? 0)
    return hypothesisWon
      ? <Tag appearance="success">Hipótesis mejor · {(p * 100).toFixed(0)}%</Tag>
      : <Tag appearance="warning">Línea base mejor · {(p * 100).toFixed(0)}%</Tag>
  }

  const fmtPct = (v: number) => `${v.toFixed(1)}%`

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-2">
          <Title as="h4">Avance por paso del funnel</Title>
          <Text fontSize="caption">% de las sesiones de cada variante que llegan al paso</Text>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Paso</th>
                <th className="py-2 px-3 text-right font-medium">{baseHeader}</th>
                <th className="py-2 px-3 text-right font-medium">{hypHeader}</th>
                <th className="py-2 px-3 text-right font-medium">Δ</th>
                <th className="py-2 pl-3 text-right font-medium">Veredicto</th>
              </tr>
            </thead>
            <tbody>
              {funnelData.map((step, index) => {
                const base = step[baseKey]
                const hyp = step[hypKey]
                const deltaPP = hyp.percentage - base.percentage
                const deltaRel = base.percentage > 0 ? ((hyp.percentage - base.percentage) / base.percentage) * 100 : 0
                const chip = verdictChip(step.bb_rope)
                return (
                  <tr key={step.step_id} className="border-b last:border-0">
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                          {index + 1}
                        </span>
                        <Text color="neutral-textHigh">{step.step_name}</Text>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      <span className="font-medium">{fmtPct(base.percentage)}</span>
                      <Text as="span" fontSize="caption" className="ml-1">({base.count.toLocaleString('es-ES')})</Text>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">
                      <span className="font-medium">{fmtPct(hyp.percentage)}</span>
                      <Text as="span" fontSize="caption" className="ml-1">({hyp.count.toLocaleString('es-ES')})</Text>
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums ${chip ? (deltaPP > 0 ? 'text-green-600' : 'text-red-600') : 'text-muted-foreground'}`}>
                      {deltaPP > 0 ? '+' : ''}{deltaPP.toFixed(1)}pp
                      <Text as="span" fontSize="caption" className="ml-1">({deltaRel > 0 ? '+' : ''}{deltaRel.toFixed(0)}%)</Text>
                    </td>
                    <td className="py-2.5 pl-3 text-right">
                      {chip ?? <Text as="span" fontSize="caption">—</Text>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <Text fontSize="caption" className="mt-3">
          El veredicto por paso usa el mismo motor y umbral que el veredicto principal; "—" = sin diferencia concluyente.
        </Text>
      </Card.Body>
    </Card>
  )
}
